/**
 * Careers openings — department filter (desktop panel + mobile drawer).
 */
class DevCareersOpenings extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const drawer = this.querySelector('dialog-component');
    const filterTrigger = this.querySelector('[data-ref="filter-trigger"]');
    const filterPanel = this.querySelector('[data-ref="filter-panel"]');

    filterTrigger?.addEventListener('click', this.#onFilterTriggerClick, { signal });
    this.addEventListener('click', this.#onFilterCloseClick, { signal });
    this.addEventListener('change', this.#onCheckboxChange, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });

    drawer?.addEventListener('dialog:open', () => {
      filterTrigger?.setAttribute('aria-expanded', 'true');
    });
    drawer?.addEventListener('dialog:close', () => {
      if (filterPanel?.hidden !== false) {
        filterTrigger?.setAttribute('aria-expanded', 'false');
      }
    });

    if (this.dataset.source === 'peopleforce') {
      this.#loadPeopleForce(signal);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {Event} event */
  #onFilterTriggerClick = (event) => {
    if (this.#isMobile()) {
      event.preventDefault();
      this.#getDrawer()?.showDialog();
      return;
    }

    event.preventDefault();
    this.#setPanelOpen(true);
  };

  /** @param {Event} event */
  #onFilterCloseClick = (event) => {
    const closeTarget = event.target instanceof Element ? event.target.closest('[data-ref="filter-close"]') : null;
    if (!closeTarget) return;

    event.preventDefault();

    if (this.#isMobile()) {
      this.#getDrawer()?.closeDialog();
      return;
    }

    this.#setPanelOpen(false);
  };

  /** @param {Event} event */
  #onDocumentClick = (event) => {
    if (this.#isMobile()) return;

    const filterPanel = this.querySelector('[data-ref="filter-panel"]');
    const filterWrap = this.querySelector('[data-ref="filter-wrap"]');
    if (!(filterPanel instanceof HTMLElement) || filterPanel.hidden) return;
    if (!(event.target instanceof Node)) return;
    if (filterWrap?.contains(event.target)) return;

    this.#setPanelOpen(false);
  };

  /** @param {Event} event */
  #onCheckboxChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-ref="filter-checkbox"]')) return;

    const value = target.dataset.value ?? '';
    const isChecked = target.checked;

    this.querySelectorAll('[data-ref="filter-checkbox"]').forEach((checkbox) => {
      if (!(checkbox instanceof HTMLInputElement)) return;
      if ((checkbox.dataset.value ?? '') === value) {
        checkbox.checked = isChecked;
      }
    });

    this.#applyFilter();
  };

  #applyFilter() {
    const selected = this.#getSelectedDepartments();
    const rows = this.querySelectorAll('[data-openings-row]');
    let visibleCount = 0;

    rows.forEach((row) => {
      if (!(row instanceof HTMLElement)) return;

      const department = row.dataset.department ?? '';
      const isVisible = selected.length === 0 || selected.includes(department);
      row.hidden = !isVisible;
      if (isVisible) visibleCount += 1;
    });

    const emptyState = this.querySelector('[data-ref="empty-state"]');
    if (emptyState instanceof HTMLElement) {
      emptyState.hidden = visibleCount > 0;
    }

    this.#updateFilterLabel(selected);
  }

  /** @returns {string[]} */
  #getSelectedDepartments() {
    const selected = new Set();

    this.querySelectorAll('[data-ref="filter-checkbox"]:checked').forEach((checkbox) => {
      if (!(checkbox instanceof HTMLInputElement)) return;
      const value = checkbox.dataset.value ?? '';
      if (value !== '') selected.add(value);
    });

    return [...selected];
  }

  /**
   * @param {string[]} selectedKeys
   */
  #updateFilterLabel(selectedKeys) {
    const allLabel = this.dataset.allDepartmentsLabel ?? 'All departments';
    let label = allLabel;

    if (selectedKeys.length === 1) {
      const checkbox = this.querySelector(
        `[data-ref="filter-panel"] [data-ref="filter-checkbox"][data-value="${selectedKeys[0]}"]`
      );
      label = checkbox instanceof HTMLInputElement ? checkbox.dataset.label ?? selectedKeys[0] : selectedKeys[0];
    } else if (selectedKeys.length > 1) {
      label = `${selectedKeys.length} departments`;
    }

    this.querySelectorAll('[data-openings-filter-trigger-text] [data-rolling-text-line]').forEach((line) => {
      line.textContent = label;
    });
  }

  /**
   * @param {boolean} isOpen
   */
  #setPanelOpen(isOpen) {
    const filterWrap = this.querySelector('[data-ref="filter-wrap"]');
    const filterTrigger = this.querySelector('[data-ref="filter-trigger"]');
    const filterPanel = this.querySelector('[data-ref="filter-panel"]');

    if (!(filterPanel instanceof HTMLElement) || !(filterTrigger instanceof HTMLButtonElement)) return;

    filterPanel.hidden = !isOpen;
    filterWrap?.classList.toggle('is-open', isOpen);
    filterTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  #isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  /**
   * @param {AbortSignal} signal
   */
  async #loadPeopleForce(signal) {
    const status = this.querySelector('[data-ref="load-status"]');
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.dataset.vacanciesUrl || '/apps/careers/vacancies', {
        headers: { Accept: 'application/json' },
        signal,
      });
      const payload = await response.json();
      if (!response.ok || payload?.ok !== true || !Array.isArray(payload.data)) {
        throw new Error('vacancies failed');
      }

      const vacancies = payload.data
        .map((item) => ({
          id: item?.id,
          title: this.#label(item?.title ?? item?.name),
          location: this.#label(item?.location),
          department: this.#label(item?.department),
        }))
        .filter((item) => item.id != null && item.id !== '' && item.title !== '');

      this.#renderPeopleForce(vacancies);
      if (status) status.hidden = true;

      if (vacancies.length === 0) {
        const empty = this.querySelector('[data-ref="empty-state"]');
        if (empty instanceof HTMLElement) {
          empty.textContent = this.dataset.emptyListLabel || empty.textContent;
          empty.hidden = false;
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (status instanceof HTMLElement) {
        const text = status.querySelector('[data-ref="load-status-text"]');
        const skeleton = status.querySelector('[data-ref="load-skeleton"]');
        if (skeleton instanceof HTMLElement) skeleton.hidden = true;
        if (text instanceof HTMLElement) {
          text.classList.remove('visually-hidden');
          text.classList.add('dev-careers-openings__empty');
          text.textContent = this.dataset.loadErrorLabel || text.textContent;
        }
        status.hidden = false;
      }
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #label(value) {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
    if (Array.isArray(value)) return this.#label(value[0]);
    if (typeof value === 'object') {
      const record = /** @type {{ name?: unknown; title?: unknown; label?: unknown }} */ (value);
      return this.#label(record.name ?? record.title ?? record.label ?? '');
    }
    return '';
  }

  /**
   * @param {string} value
   */
  #handleize(value) {
    return value
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * @param {string | number} id
   * @param {string} title
   */
  #jobHref(id, title) {
    const base = this.dataset.jobUrl || '/pages/careers-apply';
    const url = new URL(base, window.location.origin);
    const handle = this.#handleize(title);
    url.searchParams.set('vacancy', handle ? `${id}-${handle}` : String(id));
    return `${url.pathname}${url.search}${url.hash}`;
  }

  /**
   * @param {{ id: string | number; title: string; location: string; department: string }[]} vacancies
   */
  #renderPeopleForce(vacancies) {
    const list = this.querySelector('[data-openings-list]');
    const rowTemplate = this.querySelector('[data-ref="row-template"]');
    if (!(list instanceof HTMLElement) || !(rowTemplate instanceof HTMLTemplateElement)) return;

    const rowSource = rowTemplate.content.querySelector('[data-openings-row]');
    if (!(rowSource instanceof HTMLElement)) return;

    const departments = new Map();

    for (const vacancy of vacancies) {
      const row = /** @type {HTMLAnchorElement} */ (rowSource.cloneNode(true));
      row.href = this.#jobHref(vacancy.id, vacancy.title);

      const location = row.querySelector('[data-ref="location"]');
      if (location) location.textContent = vacancy.location;

      row.querySelectorAll('[data-ref="title-line"]').forEach((line) => {
        line.textContent = vacancy.title;
      });

      const department = row.querySelector('[data-ref="department"]');
      if (department instanceof HTMLElement) {
        if (vacancy.department) {
          department.hidden = false;
          department.textContent = vacancy.department;
          const handle = this.#handleize(vacancy.department);
          row.dataset.department = handle;
          row.dataset.departmentLabel = vacancy.department;
          const current = departments.get(handle) ?? { label: vacancy.department, count: 0 };
          current.count += 1;
          departments.set(handle, current);
        } else {
          department.remove();
        }
      }

      list.appendChild(row);
    }

    const departmentList = [...departments.entries()].map(([handle, info]) => ({
      handle,
      label: info.label,
      count: info.count,
    }));

    this.querySelectorAll('[data-ref="filter-list"]').forEach((filterList) => {
      if (filterList instanceof HTMLElement) this.#renderFilterList(filterList, departmentList);
    });
  }

  /**
   * @param {HTMLElement} filterList
   * @param {{ handle: string; label: string; count: number }[]} departments
   */
  #renderFilterList(filterList, departments) {
    const template = this.querySelector('[data-ref="filter-item-template"]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const itemSource = template.content.querySelector('li');
    if (!(itemSource instanceof HTMLElement)) return;

    filterList.replaceChildren();
    const prefix = filterList.dataset.idPrefix || 'filter';

    departments.forEach((department, index) => {
      const item = /** @type {HTMLElement} */ (itemSource.cloneNode(true));
      const input = item.querySelector('[data-ref="filter-checkbox"]');
      const label = item.querySelector('label');
      const id = `dev-careers-filter-${prefix}-${index}`;

      if (input instanceof HTMLInputElement) {
        input.id = id;
        input.dataset.value = department.handle;
        input.dataset.label = department.label;
        input.value = department.label;
      }
      if (label instanceof HTMLLabelElement) label.htmlFor = id;

      const labelText = item.querySelector('[data-ref="filter-label"]');
      const count = item.querySelector('[data-ref="filter-count"]');
      if (labelText) labelText.textContent = department.label;
      if (count) count.textContent = `(${department.count})`;

      filterList.appendChild(item);
    });
  }

  #getDrawer() {
    const drawer = this.querySelector('dialog-component');
    if (drawer && 'showDialog' in drawer && 'closeDialog' in drawer) {
      return /** @type {{ showDialog: () => void; closeDialog: () => void }} */ (drawer);
    }

    return null;
  }
}

if (!customElements.get('dev-careers-openings')) {
  customElements.define('dev-careers-openings', DevCareersOpenings);
}
